import { Clock, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import type { QueueItem } from '@/hooks/useGenerationQueue';

interface QueueStatusProps {
  items: QueueItem[];
  onCancel?: (queueId: string) => void;
  getEstimatedWait: (position: number, contentType: 'video' | 'image' | 'music' | 'speech') => string;
}

export function QueueStatus({ items, onCancel, getEstimatedWait }: QueueStatusProps) {
  const { t } = useTranslation();

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div
          key={item.id}
          className="flex items-center justify-between gap-3 rounded-lg border border-border/50 bg-muted/30 px-3 py-2"
        >
          <div className="flex items-center gap-3 min-w-0">
            {item.status === 'processing' ? (
              <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />
            ) : (
              <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
            )}
            
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="shrink-0 text-xs">
                  {item.content_type}
                </Badge>
                <span className="text-sm font-medium truncate">
                  {item.status === 'processing' 
                    ? t('queue.processing', 'Processing...')
                    : t('queue.position', 'Position #{{position}}', { position: index + 1 })
                  }
                </span>
              </div>
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                {item.prompt.length > 50 ? `${item.prompt.substring(0, 50)}...` : item.prompt}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {item.status === 'queued' && (
              <>
                <span className="text-xs text-muted-foreground hidden sm:inline">
                  {getEstimatedWait(index + 1, item.content_type)}
                </span>
                {onCancel && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => onCancel(item.id)}
                    title={t('queue.cancel', 'Cancel')}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
